import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button, Empty, Spin } from 'antd';
import ContractModal from '~pages/dashboard/contract/contract-modal';
import ContractDetail from '~pages/dashboard/contract/contract-list/contract-detail';
import { useQuery } from '@apollo/client';
import { QUERY_CMS_LANDLORD_CONTRACT } from './queries';
import contract from '@pages/dashboard/contract';
import { useDispatch } from 'react-redux';
import * as contractActions from '~actions/contract';
import { StageRecord } from 'common/components/svgs';

export default function LandlordContract(props) {
  const { t, landlord } = props;
  const dispatch = useDispatch();
  const {
    data: landlordContract,
    loading: getLandlordContract,
    refetch: refetchLandlordContract,
  } = useQuery(QUERY_CMS_LANDLORD_CONTRACT, {
    variables: {
      landlordSlugs: [landlord.slug],
      levels: 'LANDLORD',
      pageNumber: 1,
      pageSize: 10,
      sortBy: 'UPDATED_AT',
      sortDirection: 'DESCENDING',
    },
  });

  const [editingContract, setEditingContract] = useState(null);

  const currentContracts = useMemo(() => {
    return landlordContract?.contracts.edges.map(edge => edge.node) || [];
  }, [landlordContract]);

  const handleCloseEditContractModal = useCallback(() => {
    setEditingContract(null);
  }, []);

  const handleEditContract = useCallback(contract => {
    setEditingContract(contract);
  }, []);

  const handleDeleteContract = useCallback(
    contract => {
      dispatch(
        contractActions.deleteContract(
          { id: contract.id },
          () => {
            refetchLandlordContract();
          },
          true,
        ),
      );
    },
    [refetchLandlordContract],
  );

  return (
    <div className="landlord-contract">
      <p className="landlord-contract__header">{t('cms.contract.detail.title.contract_details')}</p>
      <div
        className="landlord-contract__content-wrap"
        style={{ border: currentContracts.length === 0 && 'none' }}
      >
        {/* empty page */}
        <If condition={currentContracts.length === 0}>
          <Spin spinning={getLandlordContract}>
            <div className="landlord-contract__empty-page-wrap">
              <div className="landlord-contract__empty-page">
                <Empty
                  description={
                    <span>
                      {t('cms.landlord.contact.add_new_contact.landlord_level.description')}
                    </span>
                  }
                >
                  <Button
                    icon="plus"
                    type="primary"
                    className="landlord-contract__empty-page__btn"
                    onClick={() => {
                      setEditingContract({ landlord });
                    }}
                  >
                    {t('cms.landlord.contact.add_new_contact.btn')}
                  </Button>
                </Empty>
              </div>
            </div>
          </Spin>
        </If>
        <If condition={currentContracts.length > 0}>
          <div className="landlord-contract__content-page">
            <For of={currentContracts} each="contract" index="index">
              <section style={{ display: 'flex', alignItems: 'flex-start' }} key={contract.id}>
                <div className="landlord-contract__icon-wrapper">
                  <StageRecord className="icon" />
                </div>
                <ContractDetail
                  contract={contract}
                  handleEditContract={() => handleEditContract(contract)}
                  handleDeleteContract={handleDeleteContract}
                  contractApplyLevel="LANDLORD"
                  source="landlord-detail"
                  t={t}
                />
              </section>
            </For>
          </div>
        </If>
      </div>

      <If condition={editingContract}>
        <ContractModal
          activeModal
          t={t}
          handleContractModal={handleCloseEditContractModal}
          editContract={editingContract}
          handleResetData={() => {}}
          contractApplyLevel="LANDLORD"
          isFillPreparedContract={false}
          onSuccessCallback={refetchLandlordContract}
          calledByLandlord={true}
        />
      </If>
    </div>
  );
}

LandlordContract.propTypes = {
  t: PropTypes.func.isRequired,
  landlord: PropTypes.object,
};

LandlordContract.defaultProps = {
  t: () => {},
  landlord: {},
};
